import React, { useEffect, useState } from 'react';
import { Card, PageHeader, TableSkeleton } from '../../components/ui';
import { Toast } from '../../components/ui/Alert';
import {
	EmailWorkflowApi,
	BoardResponse,
	WorkflowScope,
} from '../../services/emailWorkflowApi';
import { CITY_OPTIONS, facilityLabel } from './badges';
import { WorkflowTable } from './WorkflowTable';

/**
 * Approvals → Payment Request Approvals (Phase 2 of the restructure plan).
 * The APPROVER surface: view=pending (server-scoped queue) with a History
 * switch (view=history = what I approved). This is the ONLY place finance
 * acts (Mark paid / payment proof) — the requester board is read-only there.
 * Menu slug: email-payment-request-approvals (user types 2/17/30/31); the
 * server fences every write, the menu is just discovery.
 */

const PAGE_SIZE = 50;
const ROLE_LABEL: Record<string, string> = { tier1: 'tier 1', tier2: 'tier 2', accounts: 'accounts' };

export const PaymentRequestApprovals: React.FC = () => {
	const [mode, setMode] = useState<'pending' | 'history'>('pending');
	const [q, setQ] = useState('');
	const [cityId, setCityId] = useState<number | undefined>(undefined);
	const [page, setPage] = useState(1);
	const [board, setBoard] = useState<BoardResponse | null>(null);
	const [scope, setScope] = useState<WorkflowScope | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [reloadKey, setReloadKey] = useState(0);
	const [openThreadId, setOpenThreadId] = useState<string | null>(null);
	const [toast, setToast] = useState<string | null>(null);

	useEffect(() => {
		EmailWorkflowApi.getMe().then(setScope).catch(() => setScope(null));
	}, []);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		EmailWorkflowApi.getBoard({ view: mode, q: q || undefined, city_id: cityId, page, page_size: PAGE_SIZE })
			.then(res => {
				if (!cancelled) setBoard(res);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setError((err as { message?: string })?.message || 'Could not load the queue.');
					setBoard(null);
				}
			})
			.finally(() => !cancelled && setLoading(false));
		return () => {
			cancelled = true;
		};
	}, [mode, q, cityId, page, reloadKey]);

	const total = board?.total ?? 0;
	const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
	const seats = scope?.approver_at ?? [];
	// "Pending my approval" = ONLY requests at MY seat's turn. The server's
	// view=pending also returns other tiers' turns (bug — contract v2 §3), so we
	// filter by the /me seats here. Unattributed rows (no facility/tier) pass
	// through — they need human eyes. No /me (old backend) → show as-is.
	const rows = (board?.rows ?? []).filter(
		r =>
			mode !== 'pending' ||
			scope == null ||
			r.facility_key == null ||
			r.current_tier_role == null ||
			seats.some(s => s.facility_key === r.facility_key && s.role === r.current_tier_role)
	);

	return (
		<div className="p-6">
			<PageHeader title="Payment Request Approvals" totalItems={mode === 'pending' ? rows.length : total} itemType="requests" icon="✅" />
			{seats.length > 0 && (
				<p className="text-sm text-gray-500 -mt-2 mb-4">
					You act for:{' '}
					{seats
						.map(s => `${facilityLabel(s.facility_key) || s.facility_key} (${ROLE_LABEL[s.role] ?? s.role})`)
						.join(', ')}
				</p>
			)}

			{/* pending | history */}
			<div
				className="mb-4 inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
				role="group"
				aria-label="Queue view"
			>
				{([
					['pending', 'Pending my approval'],
					['history', 'History'],
				] as const).map(([id, label]) => (
					<button
						key={id}
						type="button"
						onClick={() => {
							setMode(id);
							setPage(1);
							setOpenThreadId(null);
						}}
						aria-pressed={mode === id}
						className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
							mode === id ? 'bg-white text-primary shadow-sm' : 'text-gray-500 hover:text-gray-700'
						}`}
					>
						{label}
					</button>
				))}
			</div>

			<Card className="mb-4">
				<div className="flex flex-col sm:flex-row gap-3">
					<input
						type="search"
						placeholder="Search gist / counterparty…"
						defaultValue={q}
						onKeyDown={e => {
							if (e.key === 'Enter') {
								setQ((e.target as HTMLInputElement).value);
								setPage(1);
							}
						}}
						onBlur={e => {
							setQ(e.target.value);
							setPage(1);
						}}
						className="border border-gray-200 rounded-md px-3 py-1.5 text-sm w-full sm:w-80"
					/>
					<select
						aria-label="City"
						value={cityId ?? ''}
						onChange={e => {
							setCityId(e.target.value ? Number(e.target.value) : undefined);
							setPage(1);
						}}
						className="border border-gray-200 rounded-md px-3 py-1.5 text-sm w-full sm:w-48"
					>
						{CITY_OPTIONS.map(c => (
							<option key={String(c.value)} value={c.value}>
								{c.label}
							</option>
						))}
					</select>
				</div>
			</Card>

			{loading && !board && <TableSkeleton />}

			{!loading && error && (
				<Card className="text-center py-10">
					<p className="text-red-600 font-medium">{error}</p>
					<button type="button" onClick={() => setReloadKey(k => k + 1)} className="mt-3 text-sm text-primary underline">
						Try again
					</button>
				</Card>
			)}

			{!loading && !error && rows.length === 0 && (
				<Card className="text-center py-12">
					<div className="text-4xl mb-3">🎉</div>
					<p className="text-gray-800 font-medium">
						{mode === 'pending' ? 'Nothing waiting for you' : 'No history yet'}
					</p>
					<p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
						{mode === 'pending'
							? 'When a request reaches your seat, it shows up here to act on.'
							: 'Requests you have acted on will be listed here.'}
					</p>
				</Card>
			)}

			{!error && rows.length > 0 && (
				<Card className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
					<WorkflowTable
						rows={rows}
						openThreadId={openThreadId}
						onToggle={setOpenThreadId}
						onRowChanged={msg => {
							setReloadKey(k => k + 1);
							if (msg) setToast(msg); // acting usually removes the row from this queue
						}}
						financeActions
						startIndex={(page - 1) * PAGE_SIZE}
					/>

					{totalPages > 1 && (
						<div className="flex items-center justify-between mt-4 text-sm text-gray-600">
							<span>
								Page {page} of {totalPages} · {total} total
							</span>
							<div className="flex gap-2">
								<button
									type="button"
									disabled={page <= 1}
									onClick={() => setPage(p => p - 1)}
									className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40"
								>
									Prev
								</button>
								<button
									type="button"
									disabled={page >= totalPages}
									onClick={() => setPage(p => p + 1)}
									className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40"
								>
									Next
								</button>
							</div>
						</div>
					)}
				</Card>
			)}

			{toast && (
				<Toast variant="success" position="top-center" duration={5000} onClose={() => setToast(null)}>
					{toast}
				</Toast>
			)}
		</div>
	);
};

export default PaymentRequestApprovals;
